import { useEffect, useRef } from 'react';
import { useProfile } from '@/hooks/ProfileProvider';
import { useNotifications } from '@/hooks/useNotifications';

/**
 * ÚJ JELVÉNY ÉRKEZETT → A PROFIL ÚJRATÖLTŐDIK.
 *
 * ⚠️ MÉRT HIBA JAVÍTÁSA (Geri jelezte, 2026-09-16): a jelvények KIOSZTÓDTAK
 * — az értesítések meg is jöttek —, a Jelvények oldalon mégis szürkék
 * maradtak. Az ok nem a kiosztásban volt, hanem az időrendben:
 *
 *   1. az app elindul, a profil betöltődik (`GET /api/me`) — a jelvény még
 *      nincs meg;
 *   2. a felhasználó feltölt egy profilképet, belép egy bandába, ír egy
 *      kommentet;
 *   3. a szerver HÁTTÉRBEN kiosztja a jelvényt (`awardBadgesInBackground`);
 *   4. az értesítés élő `onSnapshot`-on megérkezik — de a profil a
 *      kliensen a 1. pontbeli, RÉGI állapot maradt, és a kártyalista abból
 *      dolgozik.
 *
 * A jelvények tehát megvoltak a szerveren, csak a kliens nem tudott róluk a
 * következő indításig.
 *
 * ⚠️ AZ ÉRTESÍTÉS A JELZÉS, NEM IDŐZÍTŐ. A `notifyBadgesAwarded` a
 * jelvénydokumentumok kiírása UTÁN fut (lásd `server/src/lib/badges.ts`),
 * tehát mire az értesítés ideér, a `users/{uid}/badges` már biztosan
 * tartalmazza az új jelvényt — az újratöltés nem futhat előre.
 */
export function BadgeSync() {
  const { items } = useNotifications();
  const { refresh } = useProfile();
  /**
   * A MÁR LÁTOTT jelvény-értesítések.
   *
   * ⚠️ AZ ELSŐ PILLANATKÉPRE NEM TÖLTÜNK ÚJRA. Az `onSnapshot` induláskor a
   * MEGLÉVŐ értesítéseket is átadja; azokra reagálva minden app-indításkor
   * feleslegesen lekérnénk a profilt, ami épp az imént töltődött be.
   */
  const seen = useRef<Set<string> | null>(null);

  useEffect(() => {
    const badgeIds = items.filter((item) => item.type === 'badge_awarded').map((item) => item.id);

    if (seen.current === null) {
      seen.current = new Set(badgeIds);
      return;
    }

    const fresh = badgeIds.filter((id) => !seen.current!.has(id));
    if (fresh.length === 0) return;
    for (const id of fresh) seen.current.add(id);

    /*
      ⚠️ CSENDES frissítés, NEM `reload`. Az a globális állapotot
      `loading`-ra állítaná, amitől minden jelvényosztáskor felvillanna a
      Splash és leszerelődne a nyitott képernyő.

      A kijelentkezett esetet a `refresh` maga kezeli (nincs bejelentkezett
      felhasználó → azonnal visszatér), ezért a profil NEM függőség itt:
      különben minden frissítés újra lefuttatná ezt az effektet.
    */
    void refresh();
  }, [items, refresh]);

  return null;
}
